/**
 * PortalChoiceScreen — dual-identity chooser after login / Google.
 * Session is already contractor-default from login; select-portal must
 * send Bearer (CSRF). Failures never dead-end — retry + projects fallback.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectPortal } from '../../api/auth'
import { useAuth } from '../../context/AuthContext'
import { getToken, silentRefreshSession } from '../../api/client'
import { setPortalPref } from '../../lib/safeNextPath'
import { AuthBrandHeader } from '../../components/auth/AuthBrandHeader'

export default function PortalChoiceScreen() {
  const navigate = useNavigate()
  const { refetch } = useAuth()
  const [busy, setBusy] = useState<'contractor' | 'vendor' | null>(null)
  const [lastPortal, setLastPortal] = useState<'contractor' | 'vendor'>('contractor')
  const [error, setError] = useState<string | null>(null)

  async function ensureAccessToken(): Promise<boolean> {
    if (getToken()) return true
    return silentRefreshSession()
  }

  async function choose(portal: 'contractor' | 'vendor') {
    setBusy(portal)
    setLastPortal(portal)
    setError(null)
    try {
      const ok = await ensureAccessToken()
      if (!ok && portal === 'vendor') {
        throw new Error('Session expired — sign in again, then choose Portal for vendors.')
      }
      if (ok) {
        await selectPortal(portal)
      }
      setPortalPref(portal)
      if (ok) await refetch()
      navigate(portal === 'vendor' ? '/vendor/dashboard' : '/dashboard', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not switch')
    } finally {
      setBusy(null)
    }
  }

  function continueToProjects() {
    setPortalPref('contractor')
    navigate('/dashboard', { replace: true })
  }

  return (
    <div
      data-testid="portal-choice"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <AuthBrandHeader />
        <div
          style={{
            background: '#fff',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r)',
            padding: 24,
          }}
        >
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>Where do you want to go?</h1>
          <p style={{ fontSize: 13, color: 'var(--ink3)', margin: '0 0 20px' }}>
            This account has contractor projects and a vendor portal.
          </p>
          {error && (
            <div
              role="alert"
              data-testid="portal-choice-error"
              style={{
                color: 'var(--red)',
                fontSize: 12,
                marginBottom: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <p style={{ margin: 0 }}>{error}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  type="button"
                  className="btn b-ol"
                  data-testid="portal-choice-retry"
                  disabled={busy != null}
                  onClick={() => choose(lastPortal)}
                  style={{ fontSize: 12, padding: '6px 10px' }}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="btn b-ol"
                  data-testid="portal-choice-fallback-projects"
                  disabled={busy != null}
                  onClick={continueToProjects}
                  style={{ fontSize: 12, padding: '6px 10px' }}
                >
                  Continue to your projects
                </button>
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn b-dk"
            data-testid="portal-choice-contractor"
            disabled={busy != null}
            onClick={() => choose('contractor')}
            style={{ width: '100%', marginBottom: 10 }}
          >
            {busy === 'contractor' ? 'Opening…' : 'Your projects'}
          </button>
          <button
            type="button"
            className="btn b-ol"
            data-testid="portal-choice-vendor"
            disabled={busy != null}
            onClick={() => choose('vendor')}
            style={{ width: '100%' }}
          >
            {busy === 'vendor' ? 'Opening…' : 'Portal for vendors'}
          </button>
        </div>
      </div>
    </div>
  )
}
